/**
 * CollabBanners — Iter 175i.
 *
 * Two stacked banners shown across the top of the Website Builder shell:
 *  1. **Approvals banner** — visible when there's ≥1 pending access
 *     request from EntrepreNatives Support. Links to /approvals.
 *  2. **Deletion banner** — visible when the site has a
 *     `deletion_scheduled` field. Shows the countdown, who initiated it,
 *     and Export + Cancel/Appeal CTAs.
 *
 * Both poll on mount and again every 60s while the tab is visible. They
 * stay invisible (render null) when nothing is pending — zero clutter
 * for the happy path.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  ShieldAlert, CalendarX, Download, MailWarning, ArrowRight, X, CheckCircle2,
} from 'lucide-react';
import { toast } from 'sonner';
import { themedConfirm, themedPrompt } from '@/lib/themed-dialog';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

export default function CollabBanners() {
  const [pendingCount, setPendingCount] = useState(0);
  const [deletion, setDeletion] = useState(null);
  const [dismissed, setDismissed] = useState(() => localStorage.getItem('wb.approval_banner_dismissed_at') || '');

  const refresh = useCallback(async () => {
    try {
      const r1 = await axios.get(`${API}/builder/access-requests?status=pending`, { headers: auth() });
      setPendingCount((r1.data?.items || []).length);
    } catch (e) { /* silent */ }
    try {
      const r2 = await axios.get(`${API}/builder/sites/me/deletion-status`, { headers: auth() });
      setDeletion(r2.data?.scheduled ? r2.data : null);
    } catch (e) { /* silent */ }
  }, []);

  useEffect(() => {
    refresh();
    const onVis = () => { if (!document.hidden) refresh(); };
    document.addEventListener('visibilitychange', onVis);
    const t = setInterval(() => { if (!document.hidden) refresh(); }, 60000);
    return () => { clearInterval(t); document.removeEventListener('visibilitychange', onVis); };
  }, [refresh]);

  const dismissApprovals = () => {
    const now = String(Date.now());
    localStorage.setItem('wb.approval_banner_dismissed_at', now);
    setDismissed(now);
  };

  const cancelDeletion = async () => {
    if (!(await themedConfirm('Cancel the scheduled deletion of your site? Your data and pages stay live.'))) return;
    try {
      await axios.post(`${API}/builder/sites/me/cancel-deletion`, {}, { headers: auth() });
      toast.success('Deletion cancelled. Your site is fully active again.');
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not cancel. Contact support.');
    }
  };

  const downloadExport = async (fmt) => {
    try {
      const r = await axios.get(`${API}/builder/sites/me/export?format=${fmt}`, {
        headers: auth(), responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([r.data], { type: fmt === 'zip' ? 'application/zip' : 'application/json' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `my_site_export.${fmt}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${fmt.toUpperCase()} export`);
    } catch (e) {
      toast.error('Export failed');
    }
  };

  // Hide approvals banner if user dismissed it within the last 6 hours
  const showApprovals = pendingCount > 0 && (() => {
    if (!dismissed) return true;
    return (Date.now() - Number(dismissed)) > 6 * 60 * 60 * 1000;
  })();

  if (!showApprovals && !deletion) return null;

  return (
    <div className="space-y-2 px-4 pt-3" data-testid="collab-banners">
      {showApprovals && (
        <div
          className="flex items-center justify-between gap-3 px-4 py-3 rounded-xl border border-amber-400/30 bg-gradient-to-r from-amber-500/10 to-orange-500/10 text-amber-100"
          data-testid="approvals-banner"
        >
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <MailWarning size={18} className="text-amber-300 shrink-0" />
            <p className="text-sm">
              <strong className="text-amber-200">EntrepreNatives Support</strong> is requesting edit access to your site.
              {pendingCount > 1 && <span className="text-amber-300/85"> · {pendingCount} pending requests</span>}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Link
              to="/website-builder/approvals"
              className="px-4 py-1.5 rounded-lg text-xs font-bold uppercase tracking-[0.16em] bg-amber-400 text-amber-950 hover:bg-amber-300 transition-colors flex items-center gap-1.5"
              data-testid="approvals-banner-cta"
            >
              Review <ArrowRight size={12} />
            </Link>
            <button
              onClick={dismissApprovals}
              className="p-1.5 text-amber-300 hover:bg-amber-400/10 rounded-lg"
              title="Dismiss for 6 hours"
              data-testid="approvals-banner-dismiss"
            >
              <X size={14} />
            </button>
          </div>
        </div>
      )}

      {deletion && (
        <DeletionBanner deletion={deletion} onCancel={cancelDeletion} onExport={downloadExport} />
      )}
    </div>
  );
}

function DeletionBanner({ deletion, onCancel, onExport }) {
  const daysLeft = deletion?.scheduled_for
    ? Math.max(0, Math.ceil((new Date(deletion.scheduled_for) - new Date()) / 86400000))
    : null;
  const initiatedBy = deletion?.initiated_by || 'self';
  const masterInitiated = initiatedBy === 'master';

  return (
    <div
      className={`rounded-xl border p-4 ${masterInitiated ? 'border-red-400/40 bg-gradient-to-r from-red-500/10 to-rose-500/10 text-red-100' : 'border-amber-400/30 bg-amber-500/10 text-amber-100'}`}
      data-testid="deletion-banner"
    >
      <div className="flex items-start gap-3 flex-wrap">
        <div className={`w-9 h-9 rounded-lg ${masterInitiated ? 'bg-red-500/20 text-red-300' : 'bg-amber-500/20 text-amber-300'} flex items-center justify-center shrink-0`}>
          {masterInitiated ? <ShieldAlert size={18} /> : <CalendarX size={18} />}
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-bold text-sm">
            {masterInitiated
              ? `Your site is scheduled for deletion by EntrepreNatives Support`
              : `You scheduled your site for deletion`}
          </p>
          <p className="text-xs mt-1 opacity-90">
            <strong className="text-white">{daysLeft} days remaining</strong> · deletes on {new Date(deletion.scheduled_for).toLocaleString()}
          </p>
          {deletion.reason && (
            <p className="text-xs mt-2 italic opacity-85">"{deletion.reason}"</p>
          )}
          <div className="flex gap-2 mt-3 flex-wrap">
            <button onClick={() => onExport('zip')} className="px-3 py-1.5 rounded-lg text-xs font-bold uppercase tracking-[0.14em] bg-white/10 hover:bg-white/15 transition-colors flex items-center gap-1.5" data-testid="deletion-export-zip">
              <Download size={12} /> Export ZIP
            </button>
            <button onClick={() => onExport('json')} className="px-3 py-1.5 rounded-lg text-xs font-bold uppercase tracking-[0.14em] bg-white/10 hover:bg-white/15 transition-colors flex items-center gap-1.5" data-testid="deletion-export-json">
              <Download size={12} /> Export JSON
            </button>
            {masterInitiated ? (
              <a
                href="mailto:support@entreprenatives.com?subject=Appeal%3A%20site%20deletion"
                className="px-3 py-1.5 rounded-lg text-xs font-bold uppercase tracking-[0.14em] bg-white/10 hover:bg-white/15 transition-colors flex items-center gap-1.5"
                data-testid="deletion-appeal"
              >
                Appeal to support
              </a>
            ) : (
              <button onClick={onCancel} className="px-3 py-1.5 rounded-lg text-xs font-bold uppercase tracking-[0.14em] bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-200 transition-colors flex items-center gap-1.5" data-testid="deletion-cancel">
                <CheckCircle2 size={12} /> Cancel deletion
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
